import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';

import { errorMessage } from '@/api/client';
import { useComponents, useRunHistory } from '@/api/queries';
import { NotFoundPage } from '@/app/errors';
import { configPath, scopePath, useScopeParam } from '@/app/scope';
import { BlindBadge } from '@/components/badges';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Settings } from '@/components/icons';
import { EmptyState, ErrorNotice, Loading, Panel, Table, Th } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import type { Scope } from '@/domain/types';

import { OutcomeStrip } from './OutcomeStrip';
import { GapRow, RefusedRow, RunRow } from './RunRow';
import { buildTimeline, combinePages, runRowId, streakSentence } from './timeline';

/**
 * Run history (TDD §5.3): a component's runs newest first, each judged by the same function
 * the evaluator uses, with checks expandable, blind periods drawn as gaps, and refused
 * payloads marked "sent but rejected". Runs are kept for 90 days.
 */
export function RunHistoryPage() {
  const scope = useScopeParam();
  const { capabilityId: raw } = useParams();
  const capabilityId = raw ? decodeURIComponent(raw) : null;
  if (!scope || !capabilityId) return <NotFoundPage />;
  return <RunHistory scope={scope} capabilityId={capabilityId} />;
}

function RunHistory({ scope, capabilityId }: { scope: Scope; capabilityId: string }) {
  const history = useRunHistory(scope, capabilityId);
  const components = useComponents(scope);
  const component = components.data?.components.find(
    (c) => c.config.capability_id === capabilityId,
  );
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const combined = useMemo(() => combinePages(history.data?.pages ?? []), [history.data]);
  const items = useMemo(() => buildTimeline(combined), [combined]);

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const select = useCallback((runKey: string) => {
    setExpanded((prev) => new Set(prev).add(runKey));
    window.requestAnimationFrame(() => {
      const row = document.getElementById(runRowId(runKey));
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      row?.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      row?.querySelector('button')?.focus();
    });
  }, []);

  if (components.data && !component) return <NotFoundPage />;

  const config = component?.config;
  const state = component?.state;

  let body: React.ReactNode;
  if (history.isLoading) {
    body = <Loading label="Loading runs…" />;
  } else if (history.isError) {
    body = (
      <ErrorNotice
        title="Could not load the run history"
        message={errorMessage(history.error)}
        action={
          <Button size="sm" onClick={() => void history.refetch()}>
            Try again
          </Button>
        }
      />
    );
  } else if (combined.runs.length === 0) {
    body = (
      <Panel bodyClassName="p-0">
        <EmptyState
          title="No runs yet"
          body={
            state && state.last_observed_at === null
              ? 'This component has never reported.'
              : 'Runs are kept for 90 days.'
          }
        />
      </Panel>
    );
  } else {
    body = (
      <Panel bodyClassName="p-0">
        <div className="border-b border-line px-5 py-3">
          <OutcomeStrip items={items} onSelect={select} />
        </div>
        <Table>
          <thead>
            <tr>
              <Th className="w-12 pl-5">
                <span className="sr-only">Details</span>
              </Th>
              <Th>Judged</Th>
              <Th>Completed</Th>
              <Th className="text-right">Duration</Th>
              <Th>Driving check</Th>
              <Th className="pr-5">Source · run id</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              if (item.kind === 'run') {
                return (
                  <RunRow
                    key={item.run.run_key}
                    run={item.run}
                    expanded={expanded.has(item.run.run_key)}
                    onToggle={() => toggle(item.run.run_key)}
                    configuredChecks={config?.checks}
                  />
                );
              }
              if (item.kind === 'gap')
                return <GapRow key={`gap-${item.window.opened_at}`} window={item.window} />;
              return (
                <RefusedRow
                  key={item.entry.quarantine_id}
                  entry={item.entry}
                  expanded={expanded.has(item.entry.quarantine_id)}
                  onToggle={() => toggle(item.entry.quarantine_id)}
                />
              );
            })}
          </tbody>
        </Table>
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
          <p className="m-0 text-meta text-ink-3 tabular">
            {combined.runs.length} {combined.runs.length === 1 ? 'run' : 'runs'} loaded
          </p>
          {history.hasNextPage ? (
            <Button
              size="sm"
              onClick={() => void history.fetchNextPage()}
              loading={history.isFetchingNextPage}
            >
              Load older runs
            </Button>
          ) : (
            <span className="text-meta text-ink-3">Oldest stored run · kept for 90 days</span>
          )}
        </div>
      </Panel>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <Breadcrumb
          items={[
            { label: 'Components', to: scopePath(scope) },
            { label: config?.display_name ?? capabilityId },
          ]}
        />
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-heading font-medium min-w-0 break-words">
            {config?.display_name ?? capabilityId}{' '}
            <code className="ml-1 text-meta font-normal text-ink-3">{capabilityId}</code>
          </h2>
          <Link
            to={configPath(scope, capabilityId)}
            className="inline-flex items-center gap-1.5 text-body whitespace-nowrap"
          >
            <Settings size={14} /> Configuration
          </Link>
        </div>
        <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-body text-ink-2">
          <span>{streakSentence(state)}</span>
          {state?.blind_since ? (
            <span className="inline-flex items-center gap-1.5">
              <BlindBadge /> since <Timestamp iso={state.blind_since} />
            </span>
          ) : null}
          {config ? <span>Runs expected every {config.runner_cadence_minutes} min</span> : null}
        </p>
      </header>
      {body}
    </div>
  );
}
